import React from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { postDefinitions } from "@/data/posts";

interface SchemaMarkupProps {
  type:
    | "WebSite"
    | "WebPage"
    | "BlogPosting"
    | "Organization"
    | "Hotel"
    | "Apartment";
  data?: {
    name?: string;
    headline?: string;
    description?: string;
    image?: string;
    datePublished?: string;
    dateModified?: string;
    author?: string;
    url?: string;
    logoUrl?: string;
    /** Apartment specific */
    floorSize?: number; // m²
    numberOfBedrooms?: number;
    numberOfBathroomsTotal?: number;
    maxOccupancy?: number;
    petsAllowed?: boolean;
  };
}

const SITE_NAME = "Lerch Suites";
const SITE_LEGAL_NAME = "Aparthotel Lerch Suites";

/** Zeitpunkt des letzten Deployments – Aktualitätssignal für Suchmaschinen & KI-Systeme. */
const CONTENT_MODIFIED =
  typeof __BUILD_TIME__ === "string" ? __BUILD_TIME__ : new Date().toISOString();

/** Kanonischer Ursprung – hält @id-Werte stabil (auch in Vorschau-Domains). */
const SITE_ORIGIN = "https://lerch-suites.at";
const ORG_ID = `${SITE_ORIGIN}/#organization`;
const HOTEL_ID = `${SITE_ORIGIN}/#hotel`;
const LOGO_URL = `${SITE_ORIGIN}/favicon-512.png`;
const PRIMARY_IMAGE = `${SITE_ORIGIN}/og-image.webp`;
const TELEPHONE = "+43 664 5380244";
const EMAIL = "info@lerch-suites.at";

const POSTAL_ADDRESS = {
  "@type": "PostalAddress",
  streetAddress: "Sinsen 38a",
  postalCode: "6555",
  addressLocality: "Kappl",
  addressRegion: "Tirol",
  addressCountry: "AT",
};

/** Regionsbezug für Suchmaschinen & KI-Systeme. */
const AREA_SERVED = [
  { "@type": "Place", name: "Kappl, Paznaun" },
  { "@type": "Place", name: "Ischgl" },
  { "@type": "Place", name: "Paznauntal" },
  { "@type": "AdministrativeArea", name: "Tirol, Österreich" },
];

const CONTAINED_IN_PLACE = {
  "@type": "Place",
  name: "Paznaun, Tirol",
  address: {
    "@type": "PostalAddress",
    addressLocality: "Kappl",
    addressRegion: "Tirol",
    addressCountry: "AT",
  },
};

const CONTACT_POINT = [
  {
    "@type": "ContactPoint",
    contactType: "Reservierung",
    telephone: TELEPHONE,
    email: EMAIL,
    areaServed: "AT",
    availableLanguage: ["de", "en"],
  },
];

/** Referenz statt Duplikat – verhindert konkurrierende Organization-Blöcke. */
const PUBLISHER_REF = { "@id": ORG_ID };

/** Externe Profile & Verzeichnisse – Entitäten-Verknüpfung für Suchmaschinen und KI-Systeme. */
const SAME_AS = [
  "https://www.google.com/search?q=lerch+suites+kappl",
  "https://www.booking.com/hotel/at/aparthotel-lerch-kappl.de.html",
  "https://www.tripadvisor.at/Hotel_Review-g675592-d17606615-Reviews-Aparthotel_Lerch-Kappl_Tirol_Austrian_Alps.html",
  "https://www.kappl.com/de/urlaub-buchen/unterkunft-buchen/unterkuenfte/ISC/38bf3bbe-8c2a-4789-a07c-085fb844b4a2/lerch-suites",
  "https://www.holidaycheck.ch/hi/lerch-suites/b1329d57-2fb9-424c-9cb3-f25c1efec333",
  "https://www.traum-ferienwohnungen.de/593988/",
  "https://www.expedia.at/Landeck-Hotels-Aparthotel-Lerch.h55186244.Hotel-Beschreibung",
  "https://ch.hotels.com/ho1766959808/aparthotel-lerch-kappl-osterreich/",
];

/** Themenfelder der Entität – hilft KI-Systemen bei der Einordnung. */
const KNOWS_ABOUT = [
  "Ferienwohnungen in Kappl",
  "Boutique Aparthotel Paznaun",
  "Urlaub in Ischgl",
  "Skiurlaub Silvretta Arena",
  "Wanderurlaub Tirol",
  "Wellness und Sauna im Apartment",
  "Familienurlaub in den Alpen",
  "Ferienwohnung mit Bergblick",
];

const GEO = {
  "@type": "GeoCoordinates",
  latitude: 47.0739,
  longitude: 10.3756,
};


const AMENITIES = [
  "Wellnessbereich (Sauna)",
  "Kostenfreies WLAN",
  "Parkplatz",
  "Skiraum",
  "Frühstücksservice (Lerch Plus)",
  "Voll ausgestattete Küche in jeder Suite",
  "Balkon oder Terrasse",
  "Bergblick",
].map((name) => ({
  "@type": "LocationFeatureSpecification",
  name,
  value: true,
}));

// Map URL slugs to human-readable breadcrumb labels
const BREADCRUMB_LABELS: Record<string, string> = {
  suites: "Suiten",
  "lerch-plus": "Lerch Plus",
  wellness: "Wellness",
  erlebnisse: "Erlebnisse",
  sommer: "Sommer",
  winter: "Winter",
  "top-events": "Top-Events",
  bewertungen: "Bewertungen",
  buchen: "Buchen",
  anfrage: "Anfrage",
  "lage-anreise": "Lage & Anreise",
  sitemap: "Sitemap",
  impressum: "Impressum",
  datenschutz: "Datenschutz",
  barrierefreiheit: "Barrierefreiheit",
};

const labelize = (slug: string) => {
  const decoded = decodeURIComponent(slug);
  if (BREADCRUMB_LABELS[decoded]) return BREADCRUMB_LABELS[decoded];
  return decoded
    .replace(/-/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

const buildApartmentsContained = (siteUrl: string) =>
  postDefinitions.map((p) => {
    const acf = p.acfFields as Record<string, unknown>;
    return {
      "@type": "Apartment",
      name: p.title,
      url: `${siteUrl}/suites/${p.slug}`,
      description: p.excerpt,
      ...(acf.suite_groesse_qm && {
        floorSize: {
          "@type": "QuantitativeValue",
          value: acf.suite_groesse_qm,
          unitCode: "MTK",
        },
      }),
      ...(acf.suite_schlafzimmer && {
        numberOfBedrooms: acf.suite_schlafzimmer,
      }),
      ...(acf.suite_baeder && {
        numberOfBathroomsTotal: acf.suite_baeder,
      }),
      ...(acf.suite_personen_max && {
        occupancy: {
          "@type": "QuantitativeValue",
          maxValue: acf.suite_personen_max,
          ...(acf.suite_personen_min && { minValue: acf.suite_personen_min }),
        },
      }),
    };
  });

const SchemaMarkup: React.FC<SchemaMarkupProps> = ({ type, data = {} }) => {
  const location = useLocation();
  const currentUrl = data.url || window.location.origin + location.pathname;
  const siteUrl = window.location.origin;

  let schema: Record<string, unknown>;

  switch (type) {
    case "WebSite":
      schema = {
        "@context": "https://schema.org",
        "@type": "WebSite",
        "@id": `${SITE_ORIGIN}/#website`,
        name: data.name || SITE_NAME,
        url: siteUrl,
        inLanguage: "de-AT",
        publisher: PUBLISHER_REF,
        about: { "@id": HOTEL_ID },
        dateModified: data.dateModified || CONTENT_MODIFIED,
      };
      break;

    case "WebPage":
      schema = {
        "@context": "https://schema.org",
        "@type": "WebPage",
        "@id": `${currentUrl}#webpage`,
        name: data.name || document.title || SITE_NAME,
        url: currentUrl,
        inLanguage: "de-AT",
        isPartOf: { "@id": `${SITE_ORIGIN}/#website` },
        publisher: PUBLISHER_REF,
        about: { "@id": HOTEL_ID },
        mainEntity: { "@id": HOTEL_ID },
        primaryImageOfPage: {
          "@type": "ImageObject",
          url: data.image || PRIMARY_IMAGE,
        },
        dateModified: data.dateModified || CONTENT_MODIFIED,
        ...(data.description && { description: data.description }),
      };
      break;

    case "BlogPosting":
      schema = {
        "@context": "https://schema.org",
        "@type": "BlogPosting",
        headline: data.headline || data.name || "",
        url: currentUrl,
        mainEntityOfPage: currentUrl,
        inLanguage: "de-AT",
        ...(data.description && { description: data.description }),
        ...(data.image && { image: data.image }),
        ...(data.datePublished && { datePublished: data.datePublished }),
        ...(data.dateModified && { dateModified: data.dateModified }),
        ...(data.author && {
          author: { "@type": "Person", name: data.author },
        }),
        publisher: PUBLISHER_REF,
      };
      break;

    case "Organization":
      schema = {
        "@context": "https://schema.org",
        "@type": "Organization",
        "@id": ORG_ID,
        name: data.name || SITE_NAME,
        legalName: SITE_LEGAL_NAME,
        url: SITE_ORIGIN,
        address: POSTAL_ADDRESS,
        sameAs: SAME_AS,
        knowsAbout: KNOWS_ABOUT,
        telephone: TELEPHONE,
        email: EMAIL,
        contactPoint: CONTACT_POINT,
        areaServed: AREA_SERVED,
        logo: {
          "@type": "ImageObject",
          url: data.logoUrl || LOGO_URL,
        },
        image: data.image || PRIMARY_IMAGE,
      };
      break;


    case "Hotel":
      schema = {
        "@context": "https://schema.org",
        "@type": "Hotel",
        "@id": HOTEL_ID,
        name: data.name || SITE_NAME,
        legalName: SITE_LEGAL_NAME,
        url: SITE_ORIGIN,
        description:
          data.description ||
          "Boutique Aparthotel mit vier individuell gestalteten Suiten in Kappl im Paznaun – nur sieben Minuten von Ischgl, Tirol.",
        address: POSTAL_ADDRESS,
        geo: GEO,
        sameAs: SAME_AS,
        knowsAbout: KNOWS_ABOUT,
        telephone: TELEPHONE,
        email: EMAIL,
        contactPoint: CONTACT_POINT,
        areaServed: AREA_SERVED,
        containedInPlace: CONTAINED_IN_PLACE,
        priceRange: "€€",
        currenciesAccepted: "EUR",
        petsAllowed: false,
        checkinTime: "16:00",
        checkoutTime: "10:00",
        numberOfRooms: postDefinitions.length,
        amenityFeature: AMENITIES,
        availableLanguage: [
          { "@type": "Language", name: "Deutsch", alternateName: "de" },
          { "@type": "Language", name: "Englisch", alternateName: "en" },
        ],
        containsPlace: buildApartmentsContained(siteUrl),
        publisher: PUBLISHER_REF,
        dateModified: data.dateModified || CONTENT_MODIFIED,
        image: data.image || PRIMARY_IMAGE,
        logo: { "@type": "ImageObject", url: data.logoUrl || LOGO_URL },
      };
      break;

    case "Apartment":
      schema = {
        "@context": "https://schema.org",
        "@type": "Apartment",
        "@id": `${currentUrl}#apartment`,
        name: data.name || "",
        url: currentUrl,
        mainEntityOfPage: currentUrl,
        inLanguage: "de-AT",
        publisher: PUBLISHER_REF,
        dateModified: data.dateModified || CONTENT_MODIFIED,

        ...(data.description && { description: data.description }),
        ...(data.image && { image: data.image }),
        ...(data.floorSize && {
          floorSize: {
            "@type": "QuantitativeValue",
            value: data.floorSize,
            unitCode: "MTK",
          },
        }),
        ...(data.numberOfBedrooms !== undefined && {
          numberOfBedrooms: data.numberOfBedrooms,
        }),
        ...(data.numberOfBathroomsTotal !== undefined && {
          numberOfBathroomsTotal: data.numberOfBathroomsTotal,
        }),
        ...(data.maxOccupancy && {
          occupancy: {
            "@type": "QuantitativeValue",
            maxValue: data.maxOccupancy,
          },
        }),
        ...(data.petsAllowed !== undefined && { petsAllowed: data.petsAllowed }),
        containedInPlace: { "@id": HOTEL_ID },

      };
      break;
  }

  const pathParts = location.pathname.split("/").filter(Boolean);
  const breadcrumbSchema =
    pathParts.length > 0
      ? {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: "Startseite",
              item: siteUrl,
            },
            ...pathParts.map((part, i) => ({
              "@type": "ListItem",
              position: i + 2,
              name: labelize(part),
              item: `${siteUrl}/${pathParts.slice(0, i + 1).join("/")}`,
            })),
          ],
        }
      : null;

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
      {breadcrumbSchema && (
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      )}
    </Helmet>
  );
};

export default SchemaMarkup;
